import { useEffect, useMemo, useRef } from 'react';

function App() {
  return (
    <>
      <div className="grain" aria-hidden="true"></div>

      <header className="topbar">
        <span className="lf">
          <span className="pulse" aria-hidden="true"></span> Ixian, Inc.
        </span>
        <span className="ct">IXIAN</span>
        <span className="rt">San Francisco · 2026</span>
      </header>

      <Hero />
      <SystemSection />
      <StackSection />
      <MachinerySection />
      <OperationsSection />

      <footer className="site-footer">
        <span className="lf">Ixian, Inc.</span>
        <span className="ct">
          <a href="mailto:partner@ixiansupply.com">partner@ixiansupply.com</a>
        </span>
        <span className="rt">Ixian, Inc. · San Francisco · 2026</span>
      </footer>
    </>
  );
}

/* ── HERO ─────────────────────────────────────────────────────────── */
function Hero() {
  return (
    <section className="hero">
      <div className="hero-stage">
        <div className="component" aria-hidden="true">
          <svg className="part" viewBox="-200 -200 400 400" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="bladeFill" x1="0" y1="-1" x2="0" y2="1">
                <stop offset="0%" stopColor="rgba(176,190,197,0.10)" />
                <stop offset="50%" stopColor="rgba(176,190,197,0.04)" />
                <stop offset="100%" stopColor="rgba(176,190,197,0.10)" />
              </linearGradient>
              <linearGradient id="bladeStroke" x1="0" y1="-1" x2="0" y2="1">
                <stop offset="0%" stopColor="rgba(245,245,245,0.85)" />
                <stop offset="50%" stopColor="rgba(245,245,245,0.55)" />
                <stop offset="100%" stopColor="rgba(245,245,245,0.85)" />
              </linearGradient>
              <g id="oneBlade">
                <path
                  d="M 0 -22 C 12 -60, 30 -110, 14 -150 C 8 -164, -4 -168, -10 -158 C -22 -130, -16 -80, -8 -40 Z"
                  fill="url(#bladeFill)"
                  stroke="url(#bladeStroke)"
                  strokeWidth="1"
                  strokeLinejoin="round"
                />
                <path
                  d="M 0 -22 C 8 -60, 22 -100, 14 -150"
                  fill="none"
                  stroke="rgba(245,245,245,0.4)"
                  strokeWidth="0.4"
                />
              </g>
            </defs>

            <circle cx="0" cy="0" r="178" fill="none" stroke="rgba(176,190,197,0.10)" strokeWidth="0.6" strokeDasharray="2 6" />
            <circle cx="0" cy="0" r="148" fill="none" stroke="rgba(176,190,197,0.16)" strokeWidth="0.6" />
            <circle cx="0" cy="0" r="118" fill="none" stroke="rgba(176,190,197,0.10)" strokeWidth="0.6" strokeDasharray="2 6" />

            <circle cx="0" cy="0" r="22" fill="rgba(0,0,0,1)" stroke="url(#bladeStroke)" strokeWidth="1" />
            <circle cx="0" cy="0" r="6" fill="none" stroke="rgba(245,245,245,0.7)" strokeWidth="0.6" />
            <line x1="-4" y1="0" x2="4" y2="0" stroke="rgba(245,245,245,0.7)" strokeWidth="0.6" />
            <line x1="0" y1="-4" x2="0" y2="4" stroke="rgba(245,245,245,0.7)" strokeWidth="0.6" />

            <g>
              {Array.from({ length: 12 }, (_, i) => (
                <use key={i} href="#oneBlade" transform={`rotate(${i * 30})`} />
              ))}
            </g>
          </svg>

          <div className="light-crawl" aria-hidden="true"></div>

          <div className="callouts" aria-hidden="true">
            <div className="callout c1">
              <span className="row">
                <span className="lead"></span>
                <span className="dot"></span>
                <strong>The supply chain</strong>
              </span>
            </div>
            <div className="callout c2"><strong>Software-native</strong></div>
            <div className="callout c3"><strong>Operating, not selling</strong></div>
            <div className="callout c4">Built to compound</div>
            <div className="callout c5">San Francisco</div>
          </div>
        </div>
      </div>

      <div className="hero-foot">
        <div></div>
        <div className="wordmark-block">
          <h1 className="wordmark" aria-label="IXIAN">
            <span className="ch">I</span>
            <span className="ch">X</span>
            <span className="ch">I</span>
            <span className="ch">A</span>
            <span className="ch">N</span>
          </h1>
          <p className="tagline">An AI-native operating company</p>
        </div>
        <div className="cta-row">
          <a href="#system" className="cta">
            <span>Enter</span>
            <span className="cta-circle" aria-hidden="true"></span>
          </a>
        </div>
      </div>
      <div className="scroll-cue">SCROLL ↓</div>
    </section>
  );
}

/* ── SECTION 1: THE SYSTEM ───────────────────────────────────────── */
function SystemSection() {
  return (
    <section id="system">
      <div className="sect-head">
        <div className="sect-meta">
          <span>§ 01</span>
          <b>The System</b>
        </div>
        <h2 className="sect-title">A live procurement engine, not a tool.</h2>
      </div>

      <div className="system-grid">
        <div className="stat-stack">
          <div className="stat">
            <span className="num plain">On time.</span>
            <span className="lbl">Reliability is the only feature that matters</span>
          </div>
          <div className="stat">
            <span className="num plain">On spec.</span>
            <span className="lbl">Every drawing read. Every tolerance honored.</span>
          </div>
          <div className="stat">
            <span className="num plain">On record.</span>
            <span className="lbl">Every step traceable. Every line auditable.</span>
          </div>
        </div>

        <div className="feed" aria-hidden="true">
          <PrinciplesFeed />
        </div>
      </div>
    </section>
  );
}

function PrinciplesFeed() {
  const principles = [
    'Read everything · drawings, specs, certs',
    'Match before quoting · on capability, not catalog',
    'Quote with proof · no guesswork on lead times',
    'Ship what was promised · in the form it was promised',
    'Close the loop · cert, photo, signature on file',
    'Read everything · drawings, specs, certs',
    'Match before quoting · on capability, not catalog',
    'Quote with proof · no guesswork on lead times',
    'Ship what was promised · in the form it was promised',
    'Close the loop · cert, photo, signature on file',
  ];
  return (
    <div className="feed-track">
      {principles.map((p, i) => (
        <div className="feed-line" key={i}>
          <span className="id">·</span>
          <span className="ok">{p}</span>
        </div>
      ))}
    </div>
  );
}

/* ── SECTION 2: THE STACK ────────────────────────────────────────── */
function StackSection() {
  return (
    <section>
      <div className="sect-head">
        <div className="sect-meta">
          <span>§ 02</span>
          <b>The Stack</b>
        </div>
        <h2 className="sect-title">One system. Three disciplines.</h2>
      </div>

      <div className="stack-grid">
        <article className="panel">
          <div className="panel-hd">
            <span className="id">Orchestration · 01</span>
            <span className="live">01</span>
          </div>
          <div className="panel-body">
            <div className="orch" aria-hidden="true">
              <svg viewBox="0 0 400 360" preserveAspectRatio="xMidYMid slice">
                <g stroke="rgba(176,190,197,0.18)" strokeWidth="0.6" fill="none">
                  <line x1="0" y1="360" x2="200" y2="180" />
                  <line x1="80" y1="360" x2="200" y2="180" />
                  <line x1="160" y1="360" x2="200" y2="180" />
                  <line x1="240" y1="360" x2="200" y2="180" />
                  <line x1="320" y1="360" x2="200" y2="180" />
                  <line x1="400" y1="360" x2="200" y2="180" />
                  <line x1="0" y1="0" x2="200" y2="180" />
                  <line x1="80" y1="0" x2="200" y2="180" />
                  <line x1="160" y1="0" x2="200" y2="180" />
                  <line x1="240" y1="0" x2="200" y2="180" />
                  <line x1="320" y1="0" x2="200" y2="180" />
                  <line x1="400" y1="0" x2="200" y2="180" />
                </g>
                <g stroke="rgba(176,190,197,0.14)" strokeWidth="0.5" fill="none">
                  <line x1="0" y1="120" x2="400" y2="120" />
                  <line x1="0" y1="180" x2="400" y2="180" />
                  <line x1="0" y1="240" x2="400" y2="240" />
                </g>
                <circle cx="120" cy="180" r="2" fill="#F5F5F5" opacity="0.8" />
                <circle cx="280" cy="180" r="2" fill="#F5F5F5" opacity="0.6" />
                <circle cx="200" cy="120" r="1.5" fill="#DC2626" opacity="0.9" />
              </svg>
              <span className="core"></span>
            </div>
          </div>
          <div className="panel-ft">
            <span>Orchestration</span>
            <span>Always on</span>
          </div>
        </article>

        <article className="panel">
          <div className="panel-hd">
            <span className="id">Analysis · 02</span>
            <span className="live">02</span>
          </div>
          <div className="panel-body">
            <div className="docs" aria-hidden="true">
              <DocsTrack />
            </div>
          </div>
          <div className="panel-ft">
            <span>Analysis</span>
            <span>Spec to bid</span>
          </div>
        </article>

        <article className="panel">
          <div className="panel-hd">
            <span className="id">Comms · 03</span>
            <span className="live">03</span>
          </div>
          <div className="panel-body">
            <div className="comms" aria-hidden="true">
              <CommsTrack />
            </div>
          </div>
          <div className="panel-ft">
            <span>Comms</span>
            <span>Supplier ↔ buyer</span>
          </div>
        </article>
      </div>
    </section>
  );
}

function DocsTrack() {
  const tags = useMemo(
    () => ['DRAWING', 'SPEC', 'CERT', 'INVOICE', 'PACKAGING', 'CHEM REPT', 'TEST DATA'],
    []
  );
  const docs = useMemo(() => {
    const rows: string[] = [];
    for (let i = 0; i < 18; i++) {
      rows.push(tags[Math.floor(Math.random() * tags.length)]);
    }
    return [...rows, ...rows];
  }, [tags]);

  return (
    <div className="docs-track">
      {docs.map((tag, i) => (
        <div className="doc" key={i}>
          <div className="lines">
            <i></i><i></i><i></i>
          </div>
          <span className="badge">{tag}</span>
        </div>
      ))}
    </div>
  );
}

function CommsTrack() {
  const lines: [string, string][] = [
    ['→ supplier.aerotech.com', 'RFQ-7741 · qty 12 · need by 04.30'],
    ['← supplier.aerotech.com', 'ACK · ETA 6 wk · INC718 in stock'],
    ['→ supplier.precision-ml.com', 'RFQ-7742 · drawing rev C attached'],
    ['→ supplier.tridyne.com', 'package signed · ready for pickup'],
    ['← supplier.precision-ml.com', 'quote $48,210 · MOQ 1 · lead 4 wk'],
    ['→ supplier.maritec.com', 'requesting cert of conformance'],
    ['← supplier.tridyne.com', 'shipment 1 of 1 confirmed · 04.28'],
    ['→ contracting.officer.gov', 'bid submitted · package complete'],
  ];
  const doubled = [...lines, ...lines];

  return (
    <div className="comms-track">
      {doubled.map(([from, to], i) => (
        <div className="comms-line" key={i}>
          <span className="from">{from}</span>
          <span className="to">{to}</span>
        </div>
      ))}
    </div>
  );
}

/* ── SECTION 3: THE MACHINERY ─────────────────────────────────────── */
function MachinerySection() {
  return (
    <section>
      <div className="sect-head">
        <div className="sect-meta">
          <span>§ 03</span>
          <b>The Machinery</b>
        </div>
        <h2 className="sect-title">Sourcing. Verification. Execution.</h2>
      </div>

      <div className="machinery">
        <article className="stage-card">
          <div className="stage-meta">
            <span className="idx">01 · Sourcing</span>
            <h3>A query, fired across a graph of every supplier we've ever met.</h3>
            <p>
              The query lands as light on a network. The right node answers first; the wrong ones don't
              answer at all. Latency is the only honest score.
            </p>
          </div>
          <div className="stage-art">
            <SourcingArt />
          </div>
        </article>

        <article className="stage-card">
          <div className="stage-meta">
            <span className="idx">02 · Verification</span>
            <h3>Specifications checked. Markings applied. Every line traceable.</h3>
            <p>
              Drawings flick past faster than a human can read. Every tolerance, every revision letter,
              every callout is read once and remembered.
            </p>
          </div>
          <div className="stage-art">
            <VerificationArt />
          </div>
        </article>

        <article className="stage-card">
          <div className="stage-meta">
            <span className="idx">03 · Execution</span>
            <h3>A complete bid package, assembling itself.</h3>
            <p>
              Cover sheet, line items, certs of conformance, packaging marks. Every page ends with a
              signature only the system was supposed to be capable of.
            </p>
          </div>
          <div className="stage-art">
            <ExecutionArt />
          </div>
        </article>
      </div>
    </section>
  );
}

function SourcingArt() {
  const linesRef = useRef<SVGGElement | null>(null);
  const nodesRef = useRef<SVGGElement | null>(null);

  useEffect(() => {
    const lines = linesRef.current;
    const nodes = nodesRef.current;
    if (!lines || !nodes) return;
    const cx = 300, cy = 190;
    const N = 26;
    const points: [number, number][] = [];
    for (let i = 0; i < N; i++) {
      const a = (i / N) * Math.PI * 2 + (i % 2) * 0.18;
      const r = 90 + (i % 4) * 36 + Math.sin(i * 1.3) * 12;
      points.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r * 0.65]);
    }
    let lhtml = '';
    let nhtml = '';
    points.forEach((p, i) => {
      if (i % 2 === 0) lhtml += `<line x1="${cx}" y1="${cy}" x2="${p[0].toFixed(1)}" y2="${p[1].toFixed(1)}"/>`;
      const q = points[(i + 3) % N];
      lhtml += `<line x1="${p[0].toFixed(1)}" y1="${p[1].toFixed(1)}" x2="${q[0].toFixed(1)}" y2="${q[1].toFixed(1)}" stroke="rgba(176,190,197,0.10)"/>`;
      nhtml += `<circle cx="${p[0].toFixed(1)}" cy="${p[1].toFixed(1)}" r="2.2" fill="#F5F5F5" opacity="${(0.6 + Math.random() * 0.3).toFixed(3)}"/>`;
    });
    nhtml += `<circle cx="${cx}" cy="${cy}" r="4" fill="#DC2626"/>`;
    nhtml += `<circle cx="${cx}" cy="${cy}" r="9" fill="none" stroke="rgba(220,38,38,0.5)" stroke-width="0.8"/>`;
    lines.innerHTML = lhtml;
    nodes.innerHTML = nhtml;
  }, []);

  return (
    <svg viewBox="0 0 600 380" preserveAspectRatio="xMidYMid slice">
      <defs>
        <radialGradient id="nodeGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#F5F5F5" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#F5F5F5" stopOpacity="0" />
        </radialGradient>
      </defs>
      <g ref={linesRef} stroke="rgba(176,190,197,0.20)" strokeWidth="0.5" fill="none" />
      <g ref={nodesRef} />
      <circle cx="300" cy="190" r="0" fill="none" stroke="#DC2626" strokeWidth="1.4" opacity="0">
        <animate attributeName="r" from="0" to="320" dur="3.6s" repeatCount="indefinite" />
        <animate attributeName="opacity" from="0.9" to="0" dur="3.6s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

function VerificationArt() {
  return (
    <svg viewBox="0 0 600 380" preserveAspectRatio="xMidYMid slice">
      <g fill="none" stroke="rgba(245,245,245,0.7)" strokeWidth="0.8">
        <rect x="200" y="160" width="200" height="60" />
        <rect x="180" y="155" width="20" height="70" />
        <rect x="400" y="155" width="20" height="70" />
        <line x1="180" y1="240" x2="420" y2="240" strokeDasharray="2 4" />
        <line x1="180" y1="120" x2="420" y2="120" strokeDasharray="2 4" />
        <line x1="180" y1="120" x2="180" y2="100" />
        <line x1="420" y1="120" x2="420" y2="100" />
        <line x1="180" y1="110" x2="420" y2="110" />
        <polygon points="180,110 186,107 186,113" fill="rgba(245,245,245,0.7)" />
        <polygon points="420,110 414,107 414,113" fill="rgba(245,245,245,0.7)" />
      </g>
      <g fontFamily="JetBrains Mono" fontSize="10" fill="rgba(176,190,197,0.85)" letterSpacing="0.1em">
        <text x="300" y="100" textAnchor="middle">240.00 ±0.05</text>
        <text x="300" y="265" textAnchor="middle">P/N 7741-22A · REV C</text>
        <text x="500" y="180" fontSize="9">MAT: INC718</text>
        <text x="500" y="196" fontSize="9">FIN: PASSIVATE</text>
        <text x="80" y="180" fontSize="9">SCALE 1:2</text>
        <text x="80" y="196" fontSize="9">SHEET 01/04</text>
      </g>
      <line x1="0" y1="0" x2="0" y2="380" stroke="#DC2626" strokeWidth="0.6" opacity="0.7">
        <animate attributeName="x1" from="0" to="600" dur="5s" repeatCount="indefinite" />
        <animate attributeName="x2" from="0" to="600" dur="5s" repeatCount="indefinite" />
      </line>
    </svg>
  );
}

function ExecutionArt() {
  const stackRef = useRef<SVGGElement | null>(null);

  useEffect(() => {
    const g = stackRef.current;
    if (!g) return;
    let h = '';
    for (let i = 0; i < 7; i++) {
      const x = 140 + i * 4;
      const y = 50 + i * 14;
      h += `<rect x="${x}" y="${y}" width="320" height="220" fill="rgba(0,0,0,0.85)" stroke="rgba(245,245,245,${0.25 + i * 0.08})">
        <animate attributeName="opacity" from="0" to="1" begin="${i * 0.35}s" dur="0.6s" fill="freeze"/>
      </rect>`;
      if (i === 6) {
        for (let j = 0; j < 8; j++) {
          const ly = y + 18 + j * 22;
          h += `<line x1="${x + 18}" y1="${ly}" x2="${x + 240 - (j % 3) * 30}" y2="${ly}" stroke="rgba(176,190,197,0.45)" stroke-width="0.5">
            <animate attributeName="opacity" from="0" to="1" begin="${i * 0.35 + 0.4 + j * 0.08}s" dur="0.4s" fill="freeze"/>
          </line>`;
        }
      }
    }
    g.innerHTML = h;
  }, []);

  return (
    <svg viewBox="0 0 600 380" preserveAspectRatio="xMidYMid slice">
      <g ref={stackRef} fill="none" stroke="rgba(245,245,245,0.7)" strokeWidth="0.6" />
      <g fontFamily="JetBrains Mono" fontSize="9.5" fill="rgba(176,190,197,0.9)" letterSpacing="0.08em">
        <text x="300" y="354" textAnchor="middle">PACKAGE COMPLETE · 07 / 07</text>
      </g>
    </svg>
  );
}

/* ── SECTION 4: OPERATIONS ────────────────────────────────────────── */
function OperationsSection() {
  return (
    <section>
      <div className="sect-head">
        <div className="sect-meta">
          <span>§ 04</span>
          <b>Operations</b>
        </div>
        <h2 className="sect-title">The control room the world doesn't see.</h2>
      </div>

      <div className="ops">
        <div className="ops-cell wide tall">
          <span className="lbl">Why now</span>
          <div className="why">
            <span>01</span>
            <p>
              <strong style={{ color: 'var(--spec)', fontWeight: 500 }}>
                Supply chain still runs on PDFs, spreadsheets and phone calls.
              </strong>{' '}
              Buyers know it. Suppliers know it. Nobody's solved it because nobody wanted to do the work.
            </p>
          </div>
          <div className="why">
            <span>02</span>
            <p>
              <strong style={{ color: 'var(--spec)', fontWeight: 500 }}>
                AI finally closes the loop.
              </strong>{' '}
              Reading drawings, matching parts, writing quotes, moving packages — the cycle is now
              machine-tractable end to end.
            </p>
          </div>
          <div className="why">
            <span>03</span>
            <p>
              <strong style={{ color: 'var(--spec)', fontWeight: 500 }}>
                Operating is the moat.
              </strong>{' '}
              Tools get copied. Operators get faster with every order — and no one can buy back the years.
            </p>
          </div>
        </div>

        <div className="ops-cell">
          <span className="lbl">Form</span>
          <span className="val plain">An operating company — we carry the work end-to-end.</span>
        </div>
        <div className="ops-cell">
          <span className="lbl">Approach</span>
          <span className="val plain">Software from day one. Every order teaches the next.</span>
        </div>
        <div className="ops-cell">
          <span className="lbl">Looking for</span>
          <span className="val plain red">Suppliers and buyers who want a faster way.</span>
        </div>
        <div className="ops-cell">
          <span className="lbl">Where</span>
          <span className="val plain">San Francisco · 2026.</span>
        </div>
      </div>
    </section>
  );
}

export default App;
